import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/use-auth';
import { navItems } from './nav-items';

/**
 * The floating pill navigation at the bottom of every page.
 *
 * Bottom rather than top, on desktop as well as mobile, because that is where
 * this audience's thumbs already are — the site is read one-handed, next to a
 * stream. One component for both widths means one set of states rather than two
 * that drift apart. It is the site's ONLY navigation, and it is deliberate.
 *
 * The icon sits beside its label, and the label appears in three stages: nothing
 * below `sm` (six labelled entries will not fit on a 375 px phone), the
 * abbreviation up to `lg`, the full name above it. The accessible name is always
 * the full one, so a screen reader never gets "Promo".
 */
export function BottomNav() {
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();
  const dock = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const list = dock.current;
    const active = list?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!list || !active || typeof list.scrollTo !== 'function') return;
    list.scrollTo({
      left: active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2,
      behavior: 'instant',
    });
  }, [pathname]);

  const items = [
    ...navItems.map((item) => ({ ...item, icon: iconFor(item.to), logo: item.logo })),
    // The last entry is the account, and it is the ONE that changes: signed in
    // it is the member's own name, signed out it is the way in. Same slot either
    // way, so the bar does not reflow when a session settles.
    isAuthenticated
      ? {
          to: '/profile',
          label: user?.userName ?? 'Profil',
          shortLabel: 'Profil',
          end: false,
          icon: ProfileIcon,
          logo: undefined,
        }
      : {
          to: '/login',
          label: 'Giriş',
          shortLabel: 'Giriş',
          end: false,
          icon: ProfileIcon,
          logo: undefined,
        },
  ];

  return (
    // Fixed, and below the story viewer's z-50 so a full-screen story covers it.
    // The safe-area inset keeps it clear of the iOS home indicator; max() means
    // it still floats on a device that has no inset.
    <nav
      aria-label="Ana menü"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4"
    >
      <ul
        ref={dock}
        className={cn(
          'dock dock-scroll pointer-events-auto flex max-w-full items-center gap-0.5 rounded-full p-1.5 sm:gap-1 lg:gap-2 lg:p-2',
          // Translucent over a blur, so the page scrolling underneath stays
          // visible and the bar reads as floating rather than as a cut-off.
          'border border-white/10 bg-surface-900/80 backdrop-blur-xl',
          'shadow-[0_8px_32px_rgba(0,0,0,0.6)]',
        )}
      >
        {items.map((item) => (
          <li key={item.to} className="shrink-0">
            <NavLink
              to={item.to}
              end={item.end}
              // The visible label is abbreviated below `sm`; the accessible name
              // never is.
              aria-label={item.label}
              className={({ isActive }) =>
                cn(
                  // Icon beside the label, not above it. Items size to their
                  // content rather than to a fixed width — six labels of very
                  // different lengths would otherwise leave "Ödüller" swimming
                  // in space while "Wager Yarışmaları" gets clipped.
                  'dock-link flex flex-row items-center justify-center rounded-full transition',
                  // 44px is the floor on touch, where there is only room for the
                  // icon. From `lg` the whole bar scales up.
                  'min-h-11 min-w-11 gap-2 px-2.5 py-2',
                  'sm:px-3.5',
                  'lg:min-h-12 lg:gap-2 lg:px-4 lg:py-3',
                  isActive
                    ? 'bg-brand-500/15 text-brand-400'
                    : 'text-surface-400 hover:bg-white/5 hover:text-surface-100',
                )
              }
            >
              {({ isActive }) =>
                item.logo ? (
                  <img
                    src={item.logo.src}
                    // Bos: baglantinin erisilebilir adini zaten aria-label
                    // veriyor, burada tekrar etmek "Moon Moon" okuturdu.
                    alt=""
                    width={407}
                    height={61}
                    className={cn(
                      // Yatay bir yazi logosu (407:61), yani bu girdi ikonlu
                      // olanlardan genis duruyor. Kacinilmaz: sahibi menude
                      // logonun kendisini istedi.
                      'h-4 w-auto transition lg:h-5',
                      // Digerleri aktif halde dolu/cizgili ikon degistiriyor;
                      // bir logo bunu yapamaz. Parlaklik, renge ek olarak
                      // ikinci bir isaret veriyor.
                      isActive ? 'opacity-100' : 'opacity-60',
                    )}
                  />
                ) : (
                  <>
                    {item.icon({ active: isActive })}
                    {/* Below `sm` the icon carries it alone: six labels beside
                      their icons do not fit on a 375px phone at any readable
                      size. Between there and `lg` the abbreviations do. */}
                    <span
                      aria-hidden
                      className="hidden whitespace-nowrap text-xs font-medium leading-none sm:block lg:hidden"
                    >
                      {item.shortLabel}
                    </span>
                    <span
                      aria-hidden
                      className="hidden whitespace-nowrap text-sm font-medium leading-none lg:block"
                    >
                      {item.label}
                    </span>
                  </>
                )
              }
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/*
 * Icons are inline rather than from a package: six of them, each needing a
 * filled/outlined pair, and that pair is what carries the active state — a
 * dependency would have to be configured to do the same thing.
 *
 * Swapping fill for stroke means the active state does not rest on colour alone.
 */

/** Maps a shared nav entry to its glyph. Keeps the icons out of the list. */
function iconFor(to: string) {
  switch (to) {
    case '/':
      return HomeIcon;
    case '/trusted-sites':
      return SitesIcon;
    case '/market':
      return MarketIcon;
    case '/geecoin':
      return CoinIcon;
    case '/milestone-rewards':
      return RewardsIcon;
    case '/wager-races':
      return RacesIcon;
    case '/ticket-events':
      return TicketIcon;
    case '/moon':
      return MoonIcon;
    default:
      return PromotionsIcon;
  }
}

function ProfileIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.4 0-8 2.2-8 5v3h16v-3c0-2.8-3.6-5-8-5z" />
    </svg>
  ) : (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-5 fill-none stroke-current lg:size-5"
      strokeWidth={1.8}
      strokeLinejoin="round"
    >
      <circle cx="12" cy="7.5" r="4.2" />
      <path d="M4.8 20.2v-1.7c0-2.6 3.2-4.6 7.2-4.6s7.2 2 7.2 4.6v1.7" />
    </svg>
  );
}

function MoonIcon({ active }: { active: boolean }) {
  // Bir hilal. Ortaki daire zeminle ayni degil, KESILMIS: dolu halde ustundeki
  // aktif rengi tasimasi gerekiyor ve sabit bir dolgu, seffaf cubugun uzerinde
  // kare bir yama olarak gorunurdu.
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path
        fillRule="evenodd"
        d="M12 2a10 10 0 1 0 9.5 13.1A8 8 0 0 1 11.3 5.4 10 10 0 0 1 12 2z"
      />
    </svg>
  ) : (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-5 fill-none stroke-current lg:size-5"
      strokeWidth={1.8}
      strokeLinejoin="round"
    >
      <path d="M12 2.8a9.2 9.2 0 1 0 8.7 12.1A8.8 8.8 0 0 1 11 5a9.2 9.2 0 0 1 1-2.2z" />
    </svg>
  );
}

function HomeIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M12 3 2.5 11h3v9h5v-6h3v6h5v-9h3L12 3z" />
    </svg>
  ) : (
    <Outline>
      <path d="M4 10.5 12 4l8 6.5V20h-5v-6H9v6H4V10.5z" />
    </Outline>
  );
}

function SitesIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M12 2l8 3.5v6c0 4.6-3.2 8.5-8 10.5-4.8-2-8-5.9-8-10.5v-6L12 2zm-1 13 5.5-5.5-1.4-1.4L11 12.2 8.9 10l-1.4 1.5L11 15z" />
    </svg>
  ) : (
    <Outline>
      <path d="M12 3.1 19 6v5.5c0 4-2.8 7.5-7 9.3-4.2-1.8-7-5.3-7-9.3V6l7-2.9z" />
      <path d="m9 12 2 2 4-4" />
    </Outline>
  );
}

function RewardsIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M20 9v2H4V9h3.2A3 3 0 0 1 12 5.6 3 3 0 0 1 16.8 9H20zM5 12h6v8H6a1 1 0 0 1-1-1v-7zm8 0h6v7a1 1 0 0 1-1 1h-5v-8z" />
    </svg>
  ) : (
    <Outline>
      <path d="M4.5 9.5h15v3h-15zM6 12.5h12V19a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-6.5zM12 9.5V20" />
      <path d="M12 9.5a2.5 2.5 0 1 0-2.5-2.5M12 9.5a2.5 2.5 0 1 1 2.5-2.5" />
    </Outline>
  );
}

function RacesIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M7 4h10v1h3v3a4 4 0 0 1-3.4 4 5 5 0 0 1-2.6 2.7V17h3v3H7v-3h3v-2.3A5 5 0 0 1 7.4 12 4 4 0 0 1 4 8V5h3V4zm10 3v3.9A2 2 0 0 0 18 9V7h-1zM6 7v2a2 2 0 0 0 1 1.9V7H6z" />
    </svg>
  ) : (
    <Outline>
      <path d="M7 4.5h10V10a5 5 0 0 1-10 0V4.5z" />
      <path d="M7 6H4.5v2A3.5 3.5 0 0 0 7 11.4M17 6h2.5v2a3.5 3.5 0 0 1-2.5 3.4M12 15v4M8.5 19.5h7" />
    </Outline>
  );
}

function TicketIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h13A2.5 2.5 0 0 1 21 8.5v1a3 3 0 0 0 0 5v1a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 15.5v-1a3 3 0 0 0 0-5v-1zm11.5-.9h-2l-1 8.8h2l1-8.8z" />
    </svg>
  ) : (
    <Outline>
      <path d="M3 9a2.5 2.5 0 0 1 2.5-2.5h13A2.5 2.5 0 0 1 21 9v.8a2.6 2.6 0 0 0 0 4.4v.8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 15v-.8a2.6 2.6 0 0 0 0-4.4V9z" />
      <path d="M14.5 6.5 13 17.5" strokeDasharray="2 2.2" />
    </Outline>
  );
}

function PromotionsIcon({ active }: { active: boolean }) {
  return active ? (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current lg:size-5">
      <path d="M11.6 2.6 21 12l-9 9-9.4-9.4A2 2 0 0 1 2 10.2V4a2 2 0 0 1 2-2h6.2c.5 0 1 .2 1.4.6zM7 8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
    </svg>
  ) : (
    <Outline>
      <path d="M11.3 3.3 20.6 12.6a1 1 0 0 1 0 1.4l-6.6 6.6a1 1 0 0 1-1.4 0L3.3 11.3A1 1 0 0 1 3 10.6V4a1 1 0 0 1 1-1h6.6a1 1 0 0 1 .7.3z" />
      <circle cx="7.5" cy="7.5" r="1.4" />
    </Outline>
  );
}

function Outline({ children }: { children: React.ReactNode }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-5 fill-none stroke-current lg:size-5"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function MarketIcon({ active }: { active: boolean }) {
  return (
    <Outline>
      <path
        d="M4 9h16l-2-5H6L4 9Zm1 0v11h14V9M9 20v-7h6v7"
        fill={active ? 'currentColor' : 'none'}
        fillOpacity={active ? 0.2 : 0}
      />
      <path d="M4 9c0 3 4 3 4 0 0 3 4 3 4 0 0 3 4 3 4 0 0 3 4 3 4 0" />
    </Outline>
  );
}
function CoinIcon({ active }: { active: boolean }) {
  return (
    <Outline>
      <circle
        cx="12"
        cy="12"
        r="9"
        fill={active ? 'currentColor' : 'none'}
        fillOpacity={active ? 0.2 : 0}
      />
      <path d="M15.5 8.7A5 5 0 1 0 17 12h-5" />
    </Outline>
  );
}
